﻿// Viewport observer utilities for CSS class toggling and animation control on visibility changes.
// Attributes supported on observed elements:
// - data-viewport-class: class to add when visible
// - data-viewport-class-remove: class to remove when not visible
// - data-viewport-animation: optional; "pause" or "reset"/"cancel" when not visible (aliases supported)
//   Semantics: when out of view -> pause; when back in view and "reset" -> rewind to 0 then play.
// - data-viewport-threshold: optional; threshold value (0-1) for when to trigger
// - data-viewport-margin: optional; margin around root (e.g., "100px 0px")
// - data-viewport-threshold-out: optional; different threshold for leaving viewport
// - data-viewport-margin-out: optional; different margin for leaving viewport
// - data-viewport-ignore-while-animating: optional; "true"/"false" (default true) to ignore intersection flips while animations are running

export function inViewPort(element: HTMLElement, blazorComponent: any, callbackMethodName: String) {
	const observer = new IntersectionObserver((entries) => {
		for (const entry of entries) {
			const visible = entry.isIntersecting;
			blazorComponent.invokeMethodAsync(callbackMethodName as any, visible);
		}
	});
	observer.observe(element);
}

// Constants
const OBSERVED_ATTR = 'data-viewport-observer';
const OBSERVE_SELECTOR = '[data-viewport-class], [data-viewport-class-remove], [data-viewport-animation], [data-viewport-threshold], [data-viewport-margin], [data-viewport-threshold-out], [data-viewport-margin-out]';

// State
const lastState = new WeakMap<Element, 'in' | 'out'>();
const observersByConfig = new Map<string, IntersectionObserver>();

type AnimMode = 'pause' | 'reset' | null;

// Animation helpers
function getAnimationsDeep(node: Element): any[] {
	try {
		const fn = (node as any).getAnimations;
		if (typeof fn === 'function') {
			try { return fn.call(node, { subtree: true }); } catch { return fn.call(node); }
		}
	} catch {}
	return [];
}

function isAnimating(el: HTMLElement): boolean {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			try {
				const timing = anim.effect?.getComputedTiming?.();
				const finished = anim.playState === 'finished' || (timing && anim.currentTime !== null && anim.currentTime >= timing.endTime);
				const running = anim.playState === 'running';
				if (running && !finished) return true;
			} catch { /* no-op */ }
		}
	} catch { /* no-op */ }
	return false;
}

// Pause helper used when element goes OUT of view
function controlAnimations(el: HTMLElement, action: 'pause' | 'reset') {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			// Out-of-view behavior: only pause. Do NOT reset to 0 here.
			if (action === 'pause' || action === 'reset') {
				anim.pause();
			}
		}
	} catch { /* no-op */ }
}

// Rewind to start (used when element comes IN to view and mode is "reset")
function resetAnimations(el: HTMLElement) {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			try { anim.currentTime = 0; } catch { /* no-op */ }
		}
	} catch { /* no-op */ }
}

// Resume or start animations (paused or idle), avoid replaying finished ones unless reset moved time back.
function playAnimations(el: HTMLElement) {
	try {
		const animations = getAnimationsDeep(el);
		for (const anim of animations) {
			try {
				const timing = anim.effect?.getComputedTiming?.();
				const hasFinished = timing && anim.currentTime !== null && anim.currentTime >= timing.endTime;

				// Start if paused or idle. If finished, only play if not actually at/after end (e.g., after reset to 0).
				if ((anim.playState === 'paused' || anim.playState === 'idle') && !hasFinished) {
					anim.play();
				}
			} catch { /* no-op */ }
		}
	} catch { /* no-op */ }
}

function parseAnimMode(value: string | null): AnimMode {
	if (value == null) return null;
	const v = value.trim().toLowerCase();
	// Back-compat aliases:
	// - "play" meant "pause when out of view"
	// - "cancel" meant "reset when out of view" (now: reset when back in view)
	if (v === 'pause' || v === 'play') return 'pause';
	if (v === 'reset' || v === 'cancel') return 'reset';
	return null;
}

function parseBoolWithDefault(value: string | null, defaultValue: boolean): boolean {
	if (value == null || value === '') return defaultValue;
	const v = value.trim().toLowerCase();
	return !(v === 'false' || v === '0' || v === 'no' || v === 'off');
}

function parseThreshold(value: string | null): number {
	if (value === null) return 0; // Default threshold

	const trimmed = value.trim();

	// Handle percentage format (e.g., "50%", "25%")
	if (trimmed.endsWith('%')) {
		const percentValue = parseFloat(trimmed.slice(0, -1));
		if (isNaN(percentValue)) return 0;
		return Math.max(0, Math.min(1, percentValue / 100));
	}

	// Handle decimal format (e.g., "0.5", "1", "0.25")
	const parsed = parseFloat(trimmed);
	if (isNaN(parsed)) return 0;

	// If value is greater than 1, assume it's a percentage without % sign
	if (parsed > 1 && parsed <= 100) {
		return Math.max(0, Math.min(1, parsed / 100));
	}

	// Clamp decimal values between 0 and 1
	return Math.max(0, Math.min(1, parsed));
}

function parseRootMargin(value: string | null): string {
	if (value === null) return '0px';

	const trimmed = value.trim();

	const marginPattern = /^(-?\d+(?:\.\d+)?(?:px|%|em|rem|vh|vw)?)(\s+(-?\d+(?:\.\d+)?(?:px|%|em|rem|vh|vw)?))*$/;

	if (!marginPattern.test(trimmed)) {
		console.warn('Invalid rootMargin format:', trimmed, 'Using default "0px"');
		return '0px';
	}

	return trimmed;
}

function getObserverConfig(threshold: number, rootMargin: string, direction: 'in' | 'out'): string {
	return `${direction}:${threshold}|${rootMargin}`;
}

function createObserver(threshold: number, rootMargin: string, direction: 'in' | 'out'): IntersectionObserver {
	return new IntersectionObserver((entries) => {
		for (const entry of entries) {
			const el = entry.target as HTMLElement;
			const addClass = el.getAttribute('data-viewport-class') as string | null;
			const removeClass = el.getAttribute('data-viewport-class-remove') as string | null;
			const animMode = parseAnimMode(el.getAttribute('data-viewport-animation'));
			const ignoreWhileAnimating = parseBoolWithDefault(el.getAttribute('data-viewport-ignore-while-animating'), true);

			const state: 'in' | 'out' = entry.isIntersecting ? 'in' : 'out';
			const prev = lastState.get(el);
			const isFlip = prev !== undefined && prev !== state;

			// Suppress edge-chatter: if this is a flip and the element is currently animating, ignore this entry.
			if (ignoreWhileAnimating && isFlip && isAnimating(el)) {
				continue;
			}

			// Only process changes that match this observer's direction
			if (direction === 'in' && state === 'in' && prev !== 'in') {
				if (addClass) el.classList.add(addClass);

				// On enter: if mode is "reset", rewind then play; if "pause", just resume/start.
				if (animMode === 'reset') {
					resetAnimations(el);
					playAnimations(el);
				} else if (animMode === 'pause') {
					playAnimations(el);
				}

				lastState.set(el, state);
			} else if (direction === 'out' && state === 'out' && prev !== 'out') {
				if (removeClass) el.classList.remove(removeClass);

				// On exit: only pause (no reset here).
				if (animMode != null) controlAnimations(el, animMode);

				lastState.set(el, state);
			}
		}
	}, {
		threshold: threshold,
		rootMargin: rootMargin,
	});
}

function getOrCreateObserver(threshold: number, rootMargin: string, direction: 'in' | 'out'): IntersectionObserver {
	const configKey = getObserverConfig(threshold, rootMargin, direction);

	if (observersByConfig.has(configKey)) {
		return observersByConfig.get(configKey)!;
	}

	const observer = createObserver(threshold, rootMargin, direction);
	observersByConfig.set(configKey, observer);
	return observer;
}

// Update the ViewportObserver function
export function ViewportObserver() {
	// Fallback for environments without IntersectionObserver (very rare)
	if (!(window as any).IntersectionObserver) {
		const elements = document.querySelectorAll(OBSERVE_SELECTOR);
		for (let i = 0; i < elements.length; i++) {
			const el = elements[i] as HTMLElement;
			if (el.getAttribute(OBSERVED_ATTR) !== 'true') {
				el.setAttribute(OBSERVED_ATTR, 'true');
				const addClass = el.getAttribute('data-viewport-class');
				if (addClass) el.classList.add(addClass);
			}
		}
		return;
	}

	const elements = document.querySelectorAll(OBSERVE_SELECTOR);

	for (let i = 0; i < elements.length; i++) {
		const element = elements[i] as HTMLElement;
		if (element.getAttribute(OBSERVED_ATTR) !== 'true') {
			element.setAttribute(OBSERVED_ATTR, 'true');

			// Get threshold and rootMargin for this element
			// Parse IN configuration
			const threshold = parseThreshold(element.getAttribute('data-viewport-threshold'));
			const margin = parseRootMargin(element.getAttribute('data-viewport-margin'));

			// Parse OUT configuration (fallback to IN config if not specified)
			const thresholdOutAttr = element.getAttribute('data-viewport-threshold-out');
			const thresholdOut = thresholdOutAttr !== null ? parseThreshold(thresholdOutAttr) : threshold;

			const marginOutAttr = element.getAttribute('data-viewport-margin-out');
			const marginOut = marginOutAttr !== null ? parseRootMargin(marginOutAttr) : margin;

			// Get or create observers
			const inObserver = getOrCreateObserver(threshold, margin, 'in');
			const outObserver = getOrCreateObserver(thresholdOut, marginOut, 'out');
		
			// Observe with both observers
			inObserver.observe(element);
			outObserver.observe(element);
		}
	}
}